import { TeethSelect, validateSchema } from "@jasper/forms";
import { Button, Group, Stack, Text } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { z } from "zod";

import type { ConfigurableProduct, ProductStepProps } from "../types";
import {
  getTeethFromArch,
  ItemScopeEnum,
  manyTeethSchema,
  ORDERED_ARCHES,
  type ItemType,
} from "@jasper/client-models";
import { useTranslation } from "react-i18next";
import {
  useOrderConfiguration,
  type UpdateProductArgs,
} from "@jasper/use-cases";
import { notifications } from "@mantine/notifications";
import { useRegisterStepSave } from "../useRegisterStepSave";
import { useTeethSelectionDescription } from "./useTeethSelectionDescription";

export type TeethStepProps = ProductStepProps & {
  applyTeethSelection?: (args: {
    teeth: number[];
    product: ConfigurableProduct;
    itemType: ItemType;
  }) => Promise<UpdateProductArgs> | UpdateProductArgs;
};

const defaultApplyTeethSelection = ({
  teeth,
  product,
  itemType,
}: {
  teeth: number[];
  product: ConfigurableProduct;
  itemType: ItemType;
}): UpdateProductArgs => {
  const itemsToSave = teeth.map(tooth => {
    const existingItem =
      product.items.find(
        item => item.scope === ItemScopeEnum.tooth && item.tooth === tooth
      ) ?? product.items[0];

    return {
      tooth,
      type: itemType,
      materialCharacteristics: existingItem?.materialCharacteristics,
      inlayCoreCharacteristics: existingItem?.inlayCoreCharacteristics,
    };
  });

  return {
    productId: product.id,
    teeth: teeth,
    items: itemsToSave,
  };
};

const validateSelection = ({ value }: { value: { teeth: number[] } }) => {
  const result = validateSchema(
    z.object({
      teeth: manyTeethSchema,
    }),
    value
  );
  return result.success ? undefined : result.errors;
};

export const TeethStep = ({
  stepKey,
  products,
  orderId,
  itemType,
  applyTeethSelection = defaultApplyTeethSelection,
  registerStepSave,
}: TeethStepProps) => {
  const { t } = useTranslation("product-workflows");
  const [product] = products;
  const describeTeethSelection = useTeethSelectionDescription();

  const { updateProduct } = useOrderConfiguration({
    orderId,
  });

  const defaultValues = useMemo(
    () => ({
      product: {
        teeth: [...product.teeth],
      },
    }),
    [product.teeth]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      try {
        const updatedProductArgs = await applyTeethSelection({
          teeth: value.product.teeth,
          product,
          itemType,
        });

        await updateProduct(updatedProductArgs);
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const handleToothClick = ({
    toothId,
    selectedTeeth,
    setSelectedTeeth,
  }: {
    toothId: number;
    selectedTeeth: number[];
    setSelectedTeeth: (teeth: number[]) => void;
  }) => {
    if (selectedTeeth.includes(toothId)) {
      setSelectedTeeth(selectedTeeth.filter(id => id !== toothId));
    } else {
      setSelectedTeeth([...selectedTeeth, toothId]);
    }
  };

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <Stack gap="md" flex={1} mih={0}>
        <form.Field
          name="product"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const errors = validateSelection({ value });

              if (!errors) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return errors;
            },
            onBlur: validateSelection,
            onSubmit: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            const errors =
              typeof formErrors?.teeth === "string"
                ? t(formErrors?.teeth, {
                    keyPrefix: "steps.teeth.errors",
                    defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                  })
                : undefined;

            return (
              <Stack gap="sm">
                <TeethSelect
                  onBlur={() => field.handleBlur()}
                  teeth={field.state.value.teeth}
                  error={errors}
                  teethError={errors ? field.state.value.teeth : undefined}
                  onClick={toothId => {
                    handleToothClick({
                      toothId,
                      selectedTeeth: field.state.value.teeth,
                      setSelectedTeeth: teeth =>
                        field.setValue({ ...field.state.value, teeth }),
                    });
                  }}
                  getTeethDescription={({ teeth }) =>
                    describeTeethSelection(teeth, [
                      { type: itemType, count: teeth.length },
                    ])
                  }
                  additionalSection={
                    <Group gap="sm" justify={"center"} pos="relative">
                      <Text
                        c="dimmed"
                        size="sm"
                        style={{
                          position: "absolute",
                          left: 0,
                        }}
                      >
                        {t("title", { keyPrefix: "steps.arch" })}
                      </Text>
                      <Group>
                        {ORDERED_ARCHES.map(arch => {
                          const archTeeth = getTeethFromArch(arch);
                          const isArchSelected = archTeeth.every(tooth =>
                            field.state.value.teeth.includes(tooth)
                          );

                          return (
                            <Button
                              key={arch}
                              onBlur={() => field.handleBlur()}
                              variant={isArchSelected ? "filled" : "outline"}
                              size="xs"
                              onClick={() => {
                                const teeth = isArchSelected
                                  ? field.state.value.teeth.filter(
                                      tooth => !archTeeth.includes(tooth)
                                    )
                                  : [
                                      ...new Set([
                                        ...field.state.value.teeth,
                                        ...archTeeth,
                                      ]),
                                    ];
                                field.setValue({ ...field.state.value, teeth });
                              }}
                            >
                              {t(arch, { keyPrefix: "arches" })}
                            </Button>
                          );
                        })}
                      </Group>
                    </Group>
                  }
                />
              </Stack>
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
